import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Trash2, Loader2, ArrowRight, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function ResetVotersPage() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reset-voters/preview");
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (confirmText !== preview?.confirm_phrase) {
      toast.error(`Frasa konfirmasi tidak cocok. Ketik "${preview?.confirm_phrase}".`);
      return;
    }
    if (!window.confirm("YAKIN? Data pemilih akan DIHAPUS PERMANEN dan tidak bisa dibatalkan.")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-voters", { confirm: confirmText });
      setResult(data);
      setConfirmText("");
      toast.success("Data pemilih berhasil di-reset.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  if (loading || !preview) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const del = preview.will_delete;
  const keep = preview.will_keep;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="reset-voters-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-red-600 font-semibold">Danger Zone</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
          <Trash2 className="h-7 w-7 text-red-600" />
          Reset Data Pemilih
        </h1>
        <p className="text-slate-600 text-sm mt-2">
          Gunakan fitur ini <b>hanya</b> saat mengganti seluruh data pemilih dengan file DPS yang baru. Data area (Dusun/RW/RT), pengguna, dan berita tetap dipertahankan.
        </p>
      </header>

      {/* Prerequisite check */}
      <Card>
        <CardContent className="p-4">
          <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4" /> Syarat Reset
          </div>
          <ul className="space-y-1.5 text-sm">
            <li className="flex items-start gap-2">
              <span className={preview.can_reset ? "text-emerald-700" : "text-red-600"}>{preview.can_reset ? "✓" : "✗"}</span>
              <span>
                Semua TPS harus di fase <b>DPS</b>. Terkunci: <b className={preview.locked_tps_count > 0 ? "text-red-600" : ""}>{preview.locked_tps_count}</b>
                {preview.locked_tps_count > 0 && (
                  <span className="text-red-600 text-xs ml-1">— unlock via halaman Lock Progress dulu.</span>
                )}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-slate-400">•</span>
              <span>Ekspor DPT per-Dusun via <Link to="/app/reports" className="text-blue-700 underline">menu Laporan</Link> untuk backup manual (opsional tapi disarankan).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-slate-400">•</span>
              <span>Setelah reset, gunakan <Link to="/app/import" className="text-blue-700 underline">menu Import DPS</Link> untuk unggah file Excel baru. TPS master akan otomatis terbentuk setelah import + Sync TPS.</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Preview */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-red-600 font-semibold mb-2">Akan Dihapus</div>
            <ul className="space-y-1 text-sm">
              <PreviewRow label="Pemilih" val={del.voters} danger />
              <PreviewRow label="Perubahan Coklit (voter_changes)" val={del.voter_changes} danger />
              <PreviewRow label="Snapshot DPSHP" val={del.dpshp_snapshots} danger />
              <PreviewRow label="Master TPS" val={del.tps_master} danger />
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-2">Tetap Dipertahankan</div>
            <ul className="space-y-1 text-sm">
              <PreviewRow label="Dusun" val={keep.dusuns} />
              <PreviewRow label="RW" val={keep.rws} />
              <PreviewRow label="RT" val={keep.rts} />
              <PreviewRow label="Pengguna (users)" val={keep.users} />
              <PreviewRow label="Pengurus RW" val={keep.rw_officers} />
              <PreviewRow label="Berita" val={keep.news} />
              <PreviewRow label="Audit Log" val={keep.audit_logs} />
              <PreviewRow label="Pengaturan" val="OK" />
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Confirm */}
      <Card className="border-red-300 bg-red-50">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-2 text-sm">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-700" />
            <div>
              <div className="font-semibold text-red-900">Aksi ini tidak dapat dibatalkan.</div>
              <div className="text-red-800 text-xs mt-1">
                Ketik <code className="font-mono bg-white border border-red-300 px-1 py-0.5 rounded">{preview.confirm_phrase}</code> di bawah untuk mengaktifkan tombol reset.
              </div>
            </div>
          </div>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={preview.confirm_phrase}
            className="h-11 font-mono bg-white border-red-300 focus:border-red-600"
            data-testid="reset-confirm-input"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={load}
              className="h-10"
              data-testid="reset-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Muat Ulang
            </Button>
            <Button
              onClick={submit}
              disabled={
                resetting ||
                !preview.can_reset ||
                confirmText !== preview.confirm_phrase
              }
              className="h-10 bg-red-600 hover:bg-red-700 disabled:opacity-40 flex-1"
              data-testid="reset-submit"
            >
              {resetting ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Menghapus...</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-1.5" /> Reset Semua Data Pemilih</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="p-4 space-y-2" data-testid="reset-result">
            <div className="font-semibold text-emerald-900 flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4" /> Reset Selesai
            </div>
            <ul className="text-sm text-emerald-900 space-y-0.5">
              <li>Pemilih dihapus: <b>{result.deleted.voters.toLocaleString("id-ID")}</b></li>
              <li>Perubahan Coklit dihapus: <b>{result.deleted.voter_changes.toLocaleString("id-ID")}</b></li>
              <li>DPSHP snapshot dihapus: <b>{result.deleted.dpshp_snapshots.toLocaleString("id-ID")}</b></li>
              <li>TPS master dihapus: <b>{result.deleted.tps_master.toLocaleString("id-ID")}</b></li>
            </ul>
            <Link
              to="/app/import"
              className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-emerald-900 hover:text-emerald-700"
              data-testid="reset-goto-import"
            >
              Lanjut ke Import DPS <ArrowRight className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PreviewRow({ label, val, danger }) {
  const isNumber = typeof val === "number";
  return (
    <li className="flex items-center justify-between border-b border-slate-100 last:border-0 py-1">
      <span className="text-slate-700">{label}</span>
      <span className={`font-mono font-bold ${danger ? "text-red-700" : "text-slate-900"}`}>
        {isNumber ? val.toLocaleString("id-ID") : val}
      </span>
    </li>
  );
}
